import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useHistory } from "react-router";
import { Box, Card, CardActions, CardContent, CardHeader, Container, Grid, LinearProgress, Typography } from "@mui/material";

import Error404 from "../../../Errors/Error404";
import Error500 from "../../../Errors/Error500";

import BreadcrumbBuilder from "../BreadcrumbBuilder";
import ConfirmDialog from "../../../Common/Dialog/ConfirmDialog";
import DownloadButton from "../../../Common/Button/DownloadButton";
import ListItemMenu from "../../../Common/ListItemMenu";
import ResourceAvatar from "./ResourceAvatar";
import RenderDate from "../../../Common/Date/RenderDate";
import ResourceForm from "./ResourceForm";
import MediaViewer from "../../../Common/Media/MediaViewer";

import rmsActions from "../../../../actions/rmsActions";
import { getResource, getRmsAppCode, getError } from "../../../../reducers";

function Resource({ resource, downloadResource, onDelete, openDialog, setOpenDialog, appCode }) {
    const title = resource?.title ?? "Resource";
    const date = resource?.createdAt ? <RenderDate date={resource.createdAt} /> : "-";

    const associationCount = resource?.associations?.length || 0;
    const history = useHistory();

    return (
        <Container className="content">
            <Grid container spacing={3}>
                <Grid item xs={12}>
                    <BreadcrumbBuilder />
                </Grid>

                <Grid item xs={12}>
                    <Card className="card">
                        <CardHeader
                            disableTypography={true}
                            avatar={
                                <ResourceAvatar
                                    fileMimeType={resource.fileMimeType}
                                    associationCount={associationCount}
                                />
                            }
                            action={
                                <ListItemMenu
                                    onEdit={() => setOpenDialog("edit")}
                                    onDelete={() => setOpenDialog("delete")}
                                />
                            }
                            title={
                                <Typography component="h2" className="card-header-title">
                                    {title}
                                </Typography>
                            }
                            subheader={
                                <Typography component="span" className="card-header-subtitle">
                                    {date}
                                </Typography>
                            }
                        />
                        <CardContent>
                            <Box className="card-content">
                                <MediaViewer
                                    fileMimeType={resource?.fileMimeType}
                                    url={resource?.url}
                                />
                            </Box>
                        </CardContent>
                        <CardActions>
                            <DownloadButton
                                url={resource.url}
                                filename={resource.fileName}
                                download={downloadResource}
                            />
                        </CardActions>
                    </Card>
                </Grid>
            </Grid>
            {openDialog === "delete" && (
                <ConfirmDialog
                    open
                    title="Delete resource"
                    content={`The following resource will be deleted: ${resource.title}`}
                    confirmButtonLabel="Delete"
                    onConfirm={() => {
                        onDelete(resource.id);
                        history.replace("/portfolio/resources");
                    }}
                    onCancel={() => setOpenDialog(null)}
                    onClose={() => setOpenDialog(null)}
                />
            )}

            {openDialog === "edit" && (
                <ResourceForm
                    open
                    onClose={() => setOpenDialog(null)}
                    resource={resource}
                    appCode={appCode}
                />
            )}
        </Container>
    );
}

const ResourceContainer = ({ match }) => {
    const dispatch = useDispatch();
    const resource = useSelector((state) => getResource(state, Number(match.params.id)));
    const error = useSelector(getError);
    const appCode = useSelector(getRmsAppCode);

    const [openDialog, setOpenDialog] = useState(null);

    useEffect(() => {
        if (!!appCode) {
            dispatch(rmsActions.getResourceDetails(match.params.id, appCode));
        }
    }, [dispatch, match.params.id, appCode]);

    if (!!error) {
        if (error.errorCode === 404) {
            return <Error404 />;
        }
        if (error.errorCode === 500) {
            return <Error500 />;
        }
    }

    if (!resource) {
        return <LinearProgress />;
    }

    const handleDownload = (onSuccess, url) => dispatch(rmsActions.downloadResource(appCode, url, onSuccess));
    const handleDelete = () => dispatch(rmsActions.deleteResource(resource.id, appCode));

    return (
        <Resource
            resource={resource}
            downloadResource={handleDownload}
            onDelete={handleDelete}
            openDialog={openDialog}
            setOpenDialog={setOpenDialog}
            appCode={appCode}
        />
    );
};

export default ResourceContainer;
